<script>
import Multiselect from 'vue-multiselect'
import 'vue-multiselect/dist/vue-multiselect.min.css'
import baseInput from '../mixins/baseInput'
import debounce from 'lodash.debounce'
import { mapGetters } from 'vuex'
import InputLine from '@/components/atoms/inputlib/components/InputLine'

export default {
    name: 'InputSelect',

    components: { Multiselect, InputLine },

    mixins: [baseInput],

    props: {
        options: {
            type: Array,
            default: () => [],
        },
        searchBy: {
            type: Array,
            default: () => [],
        },
        placeholder: {
            type: String,
            default: function () {
                return this.$t('labels.placeholder.select_option')
            },
        },
        maxHeight: {
            type: Number,
            default: 300,
        },
        trackBy: null,
        label: null,
        langNamespace: null,
        customLabel: null,
        openDirection: null,
        helper: null,
        multiple: Boolean,
        noEmpty: Boolean,
        clearable: Boolean,
        small: Boolean,
        head: Boolean,
        icon: Boolean,
        searchable: Boolean,
        loading: Boolean,
        combo: Boolean,
        remote: Boolean,
        afterListText: String,
    },

    data() {
        return {
            choices: [],
            comboModel: null,
            per_page: 10,
            page: 1,
            innerLoading: false,
            isMax: false,
            reset: false,
            isOpen: false,
            search: null,
        }
    },

    computed: {
        ...mapGetters(['smDevice']),
        customSearch() {
            return (this.combo || this.searchable) && !this.action && !!this.searchBy.length
        },
    },

    watch: {
        options(options) {
            if (this.remote) this.choices = [...options]
        },
    },

    mounted() {
        if (this.action) {
            this.getOptions()
            const el = document
                .getElementById(`select-${this.uid}`)
                .getElementsByClassName('multiselect__content-wrapper')[0]
            el.addEventListener('scroll', this.scrollHandler)
        } else {
            this.choices = [...this.options]
        }
    },

    destroyed() {
        if (this.action) {
            const el = document.getElementById(`select-${this.uid}`)
            if (el)
                el.getElementsByClassName('multiselect__content-wrapper')[0].removeEventListener(
                    'scroll',
                    this.scrollHandler
                )
        }
    },
    methods: {
        open() {
            this.isOpen = true
        },

        close() {
            this.isOpen = false
            this.$nextTick(() => {
                this.$emit('close', this.value)
            })
        },

        selected(val) {
            if (!this.disabled && !this.readonly) this.$emit('input', val)
        },

        resetSelect() {
            if (!this.disabled && !this.readonly) {
                this.$emit('reset')
                this.$emit('input', null)
            }
        },

        getOptions() {
            this.page = 1

            let params = {
                per_page: this.per_page,
                page: this.page,
                search: null,
            }
            if (this.action) {
                this.$store.dispatch(this.action, params).then((res) => {
                    this.choices = res
                })
            }
        },

        selectOption(close = true) {
            if (this.combo) {
                if (!this.disabled && !this.readonly) {
                    this.selected(
                        this.comboModel?.length
                            ? this.trackBy && this.label
                                ? {
                                      [this.trackBy]: '_',
                                      [this.label]: this.comboModel,
                                  }
                                : this.comboModel
                            : null
                    )
                }
                if (close) this.$refs.multiselect.deactivate()
            }
        },

        searchHandler(value) {
            this.search = value
            if (this.combo) {
                this.comboModel = value
            }
            if (this.remote) {
                this.choices = []
                this.$emit('search', value)
                return
            }
            if (!this.action && !this.customSearch) return
            if (this.action) {
                this.search = value
                this.page = 1
                if (this.search.length >= 2) {
                    this.reset = true
                    this.innerLoading = true
                    this.$store
                        .dispatch(this.action, {
                            search: this.search,
                            per_page: this.per_page,
                            page: this.page,
                        })
                        .then((res) => {
                            this.choices = res
                            this.innerLoading = false
                        })
                        .catch(() => {
                            this.innerLoading = false
                        })
                } else {
                    if (this.reset) {
                        this.getOptions()
                        this.reset = false
                    }
                }
            } else if (this.customSearch) {
                this.choices = this.options.filter((option) =>
                    this.searchBy.length
                        ? this.searchBy.some((searchKey) =>
                              option[searchKey]
                                  ?.toString()
                                  ?.toLowerCase()
                                  .includes(value.toLowerCase())
                          )
                        : false
                )
            }
        },

        scrollHandler: debounce(function () {
            const el = document
                .getElementById(`select-${this.uid}`)
                .getElementsByClassName('multiselect__content-wrapper')[0]
            const currentPos = el.scrollHeight - el.clientHeight
            if (el.scrollTop <= currentPos && currentPos - 5 <= el.scrollTop && !this.isMax) {
                this.page++

                let params = {
                    per_page: this.per_page,
                    page: this.page,
                }

                this.$store
                    .dispatch(this.action, params)
                    .then((res) => {
                        if (!res || !res.length) this.isMax = true
                        this.choices = [...this.choices, ...res]
                    })
                    .catch(() => {
                        this.isMax = true
                    })
            }
        }),
    },
}
</script>

<template lang="pug">


.input-select(:id="`select-${uid}`" :class="{'select-sm': small, 'search-select': searchable || combo, 'visible-clear-button': !readonly && value && (!noEmpty && clearable)}")
    label(:for="uid" :data-disabled="disabled" v-if="(hasDefaultSlot || helper) && !head")
        slot
        sup(v-if="required") *
        span.input-helper(v-if="helper") {{helper}}
    multiselect(
        ref="multiselect"
        :id="uid"
        :required="required"
        :aria-label="placeholder"
        :placeholder="placeholder"
        :disabled="disabled"
        :readonly="readonly"
        @input="selected"
        @open="open"
        @close="close"
        @search-change="searchHandler"
        @keyup.enter.native="selectOption"
        @input.native="selectOption(false)"
        :value="internalValue"
        :options="choices"
        :multiple="multiple"
        :class="{'multi-select': multiple, 'is-invalid': hasError}"
        :show-labels="false"
        :track-by="trackBy"
        :label="label"
        :allow-empty="!noEmpty"
        :searchable="searchable || combo"
        :custom-label="customLabel"
        :open-direction="openDirection"
        :max-height="maxHeight"
        :internal-search="!customSearch && !remote"
        :loading="innerLoading || loading"
    )
        template(v-slot:singleLabel="prop")
            slot(name="singleLabel" v-bind:option="prop.option")
        template(v-slot:option="prop")
            slot(name="option" v-bind:option="prop.option")
        template(v-slot:noResult="prop")
            template(v-if="combo") {{ $t('labels.combo.no_elements') }}
        template(v-if="afterListText" v-slot:afterList)
            li.px-3.py-2
                span.text-sm.text-73 {{ afterListText }}
    button.btn.btn-reset.btn-xs.text-73(@click="resetSelect" v-if="!readonly && value && (!noEmpty && clearable)" :disabled="disabled")
        icon(icon="fa-solid fa-times" size="sm")
    .invalid-feedback(
        v-if="hasError"
        :class="{'cursor-pointer': needTooltip}"
        v-html="hintText"
        ref="feedback"
        v-tooltip.hover.bottom.html="needTooltip ? hintText : undefined")
</template>
